/**
 * Typed, synchronous, fire-and-forget event emitter.
 * Handlers fire in registration order. No async, no bubbling.
 */

import type { EngineEventMap } from '../shared/types.js';
import { Logger } from './Logger.js';

type Handler<T> = (data: T) => void;

export class EventBus {
  private handlers = new Map<string, Handler<any>[]>();

  on<K extends keyof EngineEventMap>(
    event: K,
    handler: Handler<EngineEventMap[K]>,
  ): () => void {
    const key = event as string;
    let list = this.handlers.get(key);
    if (!list) {
      list = [];
      this.handlers.set(key, list);
    }
    list.push(handler);

    return () => {
      const arr = this.handlers.get(key);
      if (arr) {
        const idx = arr.indexOf(handler);
        // Copy-on-remove (never splice in place): emit() iterates the live
        // array without snapshotting, so an unsubscribe during dispatch must
        // leave the array being iterated untouched. Removal is rare; emits are
        // hot — this moves the allocation from every emit to every off().
        if (idx !== -1) {
          const next = arr.slice();
          next.splice(idx, 1);
          this.handlers.set(key, next);
        }
      }
    };
  }

  once<K extends keyof EngineEventMap>(
    event: K,
    handler: Handler<EngineEventMap[K]>,
  ): () => void {
    const wrapper = (data: EngineEventMap[K]) => {
      unsub();
      handler(data);
    };
    const unsub = this.on(event, wrapper as Handler<EngineEventMap[K]>);
    return unsub;
  }

  emit<K extends keyof EngineEventMap>(event: K, data: EngineEventMap[K]): void {
    const list = this.handlers.get(event as string);
    if (!list || list.length === 0) return;
    // No per-emit snapshot: off() is copy-on-remove (see on()), so this array
    // is immutable w.r.t. removals — a handler unsubscribing mid-emit swaps a
    // NEW array into the map and this iteration proceeds over the old one
    // (identical semantics to the old slice()). The captured length keeps
    // handlers added mid-emit out of this dispatch, as before. Rationale:
    // emit runs at 60 Hz across every subsystem; the snapshot was a per-emit
    // allocation on the engine's hottest shared path.
    for (let i = 0, n = list.length; i < n; i++) {
      try {
        list[i](data);
      } catch (err) {
        // Isolate handler errors: one throwing handler must not prevent
        // subsequent handlers from running.
        Logger.error('EventBus', `Handler error on "${event as string}": ${err}`);
      }
    }
  }

  clear(event?: keyof EngineEventMap): void {
    if (event) {
      this.handlers.delete(event as string);
    } else {
      this.handlers.clear();
    }
  }
}
